'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Locale, defaultLocale } from './i18n';
import { setLang as setKbLang } from '@/components/Kb/i18n';
import zhCN from '@/locales/zh-CN.json';
import { log, debug, warn, error } from './logger';

interface Translations {
    [key: string]: string | Translations;
}

interface LanguageContextType {
    locale: Locale;
    setLocale: (locale: Locale) => void;
    t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LOCALE_STORAGE_KEY = 'preferred-locale';

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<Locale>(defaultLocale);
    const [translations, setTranslations] = useState<Translations>(zhCN as unknown as Translations);

    // Load translations for current locale
    useEffect(() => {
        async function loadTranslations() {
            try {
                const module = await import(`@/locales/${locale}.json`);
                setTranslations(module.default);
            } catch (err) {
                error(`Failed to load translations for ${locale}:`, error);
            }
        }
        loadTranslations();
    }, [locale]);

    // Load saved locale preference on mount
    useEffect(() => {
        const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
        if (saved && (saved === 'zh-CN' || saved === 'en-US')) {
            setLocaleState(saved as Locale);
        }
    }, []);

    const setLocale = (newLocale: Locale) => {
        setLocaleState(newLocale);
        localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
        setKbLang(newLocale === 'zh-CN' ? 'zh' : 'en');
    };

    // Translation function with dot notation support and interpolation
    const t = (key: string, params?: Record<string, string | number>): string => {
        const keys = key.split('.');
        let value: any = translations;

        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                warn(`Translation key not found: ${key}`);
                return key;
            }
        }

        if (typeof value !== 'string') return key;

        // Replace placeholders like {name}, {path}
        if (params) {
            return value.replace(/\{(\w+)\}/g, (_, paramKey) => {
                return params[paramKey]?.toString() ?? `{${paramKey}}`;
            });
        }

        return value;
    };

    return (
        <LanguageContext.Provider value={{ locale, setLocale, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}
